import React, { useMemo } from 'react';

import { PickerAspectSelector } from 'features/menu/components/product-detail/picker-aspect-selector';
import { ProductCombo } from 'features/menu/components/product-detail/product-combo';
import { ProductCta } from 'features/menu/components/product-detail/product-cta';
import { ProductDetailHeader } from 'features/menu/components/product-detail/product-detail-flow/product-detail-header';
import { ProductItem } from 'features/menu/components/product-detail/product-detail-flow/product-item';
import {
  ProductHomeInnerLayout,
  ProductHomeLayoutContainer,
  ProductInformationSection,
  ProductSelectionsContainer,
} from 'features/menu/components/product-detail/product-home/styled';
import { ProductInformation } from 'features/menu/components/product-detail/product-information';
import { ProductNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/product-nutrition-information';
import { ProductOverview } from 'features/menu/components/product-detail/product-overview';
import {
  ProductCustomizationEvents,
  transformProductWizardStepToEventAttr,
} from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { ProductWizardStep } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { capitalize } from 'lodash-es';
import { ScrollView } from 'react-native';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import FullWidthAutoHeightImage from 'components/picture/full-width-auto-height-image';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { getFirstOption, isCombo, isItem } from 'state/unified-menu/utils';
import { primitive } from 'styles/constants';

export const ProductHome = () => {
  const isMobile = useIsMobileBreakpoint();

  const { currentFlowStep, cartIdEditing, id } = useProductWizardFlow();
  const {
    backgroundColor: takeoverBg,
    backgroundImage: takeoverBgImage,
    summaryPageBackgroundImage: takeoverSummaryPageBgImage,
  } = useAppTakeoverPdp(id);

  const { menuObject, selectedProduct } = useProductWizardContext();

  const selectedProductName = selectedProduct.name?.locale ?? '';

  const getHeaderTitle = () => {
    if (currentFlowStep.step !== ProductWizardStep.PickerReview) {
      return selectedProductName;
    }

    if (isCombo(selectedProduct)) {
      return t('reviewYourMeal');
    }

    return t('reviewYourSelection');
  };

  const productOverviewTitle =
    currentFlowStep.step === ProductWizardStep.PickerReview ? selectedProductName : undefined;

  const showInfo =
    currentFlowStep.step === ProductWizardStep.ProductHome ||
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    cartIdEditing;

  const showProductUpsell =
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    isCombo(menuObject) ||
    isItem(menuObject);

  const showProductInformation = currentFlowStep.step === ProductWizardStep.ProductHome;

  const eventAttributes = useMemo<{ [key: string]: string } | undefined>(() => {
    // Picker Aspect Selection Step
    if (currentFlowStep.step === ProductWizardStep.PickerSelection) {
      return {
        name: ProductCustomizationEvents.PRODUCT_CUSTOMIZATION,
        comboSlotType: capitalize(
          transformProductWizardStepToEventAttr(ProductWizardStep.PickerSelection)
        ),
      } as Record<string, string>;
    }

    // Product Home Step
    return {
      name: transformProductWizardStepToEventAttr(currentFlowStep.step),
    } as Record<string, string>;
  }, [currentFlowStep.step]);

  return (
    <>
      <ProductDetailHeader title={getHeaderTitle()} eventAttributes={eventAttributes} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, backgroundColor: takeoverBg ?? primitive.$white }}
      >
        {takeoverBgImage && currentFlowStep.step !== ProductWizardStep.PickerReview && (
          <FullWidthAutoHeightImage source={takeoverBgImage} style={{ position: 'absolute' }} />
        )}
        {(takeoverBgImage || takeoverSummaryPageBgImage) &&
          currentFlowStep.step === ProductWizardStep.PickerReview && (
            <FullWidthAutoHeightImage
              source={takeoverBgImage || takeoverSummaryPageBgImage}
              style={{ position: 'absolute' }}
            />
          )}
        {showInfo && <ProductOverview productTitle={productOverviewTitle} />}
        <ProductSelectionsContainer testID="product-home-customization-panel">
          <ProductHomeLayoutContainer isFullContainer={isMobile}>
            <ProductHomeInnerLayout $isFullContainer={isMobile}>
              <ProductHomeContent />
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        </ProductSelectionsContainer>
        {showInfo && (
          <ProductHomeLayoutContainer
            isFullContainer={isMobile}
            backgroundColor={takeoverBg ?? primitive.$mayoEggWhite}
          >
            <ProductHomeInnerLayout>
              <ProductInformationSection>
                {showProductUpsell && <ProductUpsell />}
                {showProductInformation && <ProductInformation selectedProduct={selectedProduct} />}
                <ProductNutritionInformation selectedProduct={selectedProduct} />
              </ProductInformationSection>
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        )}
      </ScrollView>
      <ProductCta />
    </>
  );
};

export const ProductHomeContent = () => {
  const { currentFlowStep, cartIdEditing } = useProductWizardFlow();
  const { menuObject } = useProductWizardContext();

  if (isItem(menuObject)) {
    return <ProductItem />;
  }

  if (
    isCombo(menuObject) ||
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    !!cartIdEditing
  ) {
    return <ProductCombo />;
  }

  const isFirstAspect = currentFlowStep.step === ProductWizardStep.ProductHome;
  const pickerAspectId = currentFlowStep.pickerAspectKey || getFirstOption(menuObject).entryId;
  return <PickerAspectSelector pickerAspectId={pickerAspectId} isFirstAspect={isFirstAspect} />;
};
